import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon, Globe, Palette, Link2, Bell, Users, BookOpen,
  Database, Shield, Send, Printer, TrendingUp, Power, Key, Activity, Package, RefreshCw
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';

import { AutonomousProcessesTab } from '../ai/index';
import { TrendDiscoveryTab } from './TrendDiscoveryTab';
import { PrintAgentPanel } from './PrintAgentPanel';

import { GeneralTab } from './tabs/GeneralTab';
import { AppearanceTab } from './tabs/AppearanceTab';
import { ConnectionsTab } from './tabs/ConnectionsTab';
import { MlControlTab } from './tabs/MlControlTab';
import { CredentialsTab } from './tabs/CredentialsTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { UsersRolesPage } from './users-roles';
import { BusinessRulesTab } from './tabs/BusinessRulesTab';
import { MarkupRulesTab } from './tabs/MarkupRulesTab';
import { RelistTab } from './tabs/RelistTab';
import { TelegramTab } from './tabs/TelegramTab';
import { DataSourcesTab } from './tabs/DataSourcesTab';
import { LabelHistoryTab } from './tabs/LabelHistoryTab';
import { AuditTab } from './tabs/AuditTab';
import { WarehouseTab } from './tabs/WarehouseTab';

const tabs = [
  { key: 'general', icon: SettingsIcon },
  { key: 'appearance', icon: Palette },
  { key: 'connections', icon: Link2 },
  { key: 'mlControl', icon: Activity },
  { key: 'apiCredentials', icon: Key },
  { key: 'automations', icon: Power },
  { key: 'notifications', icon: Bell },
  { key: 'users', icon: Users },
  { key: 'businessRules', icon: BookOpen },
  { key: 'dataSources', icon: Database },
  { key: 'telegram', icon: Send },
  { key: 'markupRules', icon: TrendingUp },
  { key: 'relist', icon: RefreshCw },
  { key: 'trendDiscovery', icon: Globe },
  { key: 'labels', icon: Printer },
  { key: 'warehouse', icon: Package },
  { key: 'audit', icon: Shield },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { theme, setTheme, language, setLanguage } = useUIStore();
  const can = useCan();
  const [activeTab, setActiveTab] = useState('general');

  const allowedTabs = visibleTabs('settings', tabs, can);
  const active = pickActiveTab('settings', tabs, activeTab, can);
  useEffect(() => {
    if (active && active !== activeTab) setActiveTab(active);
  }, [active, activeTab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('settings.title')}</h1>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tab list */}
        <div className="lg:w-56 shrink-0">
          <nav className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
            {allowedTabs.map(({ key, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors border-b border-[var(--color-border)] last:border-0
                  ${active === key
                    ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)] font-medium'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
                  }`}
              >
                <Icon size={18} />
                {t(`settings.${key}`)}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="flex-1 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-6">
          {active === 'general' && <GeneralTab t={t} language={language} setLanguage={setLanguage} />}
          {active === 'appearance' && <AppearanceTab t={t} theme={theme} setTheme={setTheme} />}
          {active === 'connections' && <ConnectionsTab t={t} />}
          {active === 'mlControl' && <MlControlTab />}
          {active === 'apiCredentials' && <CredentialsTab t={t} />}
          {active === 'automations' && <AutonomousProcessesTab />}
          {active === 'notifications' && <NotificationsTab t={t} />}
          {active === 'users' && <UsersRolesPage />}
          {active === 'businessRules' && <BusinessRulesTab t={t} />}
          {active === 'dataSources' && <DataSourcesTab t={t} />}
          {active === 'telegram' && <TelegramTab t={t} />}
          {active === 'markupRules' && <MarkupRulesTab t={t} />}
          {active === 'relist' && <RelistTab />}
          {active === 'trendDiscovery' && <TrendDiscoveryTab />}
          {active === 'labels' && (
            <div className="space-y-8">
              <PrintAgentPanel />
              <LabelHistoryTab t={t} />
            </div>
          )}
          {active === 'warehouse' && <WarehouseTab />}
          {active === 'audit' && <AuditTab t={t} />}
        </div>
      </div>
    </div>
  );
}
